import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * Only removes associated gift wrapping if the item being deleted actually has gift wrapping.
   * Uses unique cart_add_id to ensure we only remove gift wrapping for this specific cart item.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    /** @type {string | null} */
    let cartAddId = null;

    if (cartItemRowToRemove) {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        const itemToRemove = cart.items[line - 1];
        if (itemToRemove) {
          const isGiftWrapping = itemToRemove.properties && itemToRemove.properties['_main_product_id'];
          if (!isGiftWrapping && itemToRemove.properties && itemToRemove.properties['_cart_add_id']) {
            cartAddId = itemToRemove.properties['_cart_add_id'];
          }
        }
      } catch (error) {
        console.error('Failed to fetch cart:', error);
      }
    }

    await this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    if (cartAddId) {
      await this.#removeAssociatedGiftWrapping(cartAddId);
    }
  }

  /**
   * Removes gift wrapping items associated with a specific cart addition.
   * Uses the unique cart_add_id to identify which gift wrapping to remove.
   * @param {string} cartAddId - The unique cart add ID.
   */
  async #removeAssociatedGiftWrapping(cartAddId) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (!cart.items || cart.items.length === 0) return;

      /** @type {number[]} */
      const giftWrappingLinesToRemove = [];

      cart.items.forEach((/** @type {any} */ item, /** @type {number} */ index) => {
        if (item.properties) {
          const itemCartAddId = item.properties['_cart_add_id'];
          const isGiftWrapping = item.properties['_main_product_id'];
          if (isGiftWrapping && itemCartAddId === cartAddId) {
            giftWrappingLinesToRemove.push(index + 1);
          }
        }
      });

      giftWrappingLinesToRemove.sort((a, b) => b - a);

      for (const giftWrappingLine of giftWrappingLinesToRemove) {
        await this.updateQuantity({
          line: giftWrappingLine,
          quantity: 0,
          action: 'clear',
        });
      }
    } catch (error) {
      console.error('Failed to remove associated gift wrapping:', error);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @returns {Promise<void>} A promise that resolves when the update is complete.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        if (config.action === 'change' && quantity > 0) {
          setTimeout(() => {
            this.#updateAssociatedGiftWrappingQuantity(line, quantity);
          }, 100);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantity of gift wrapping items associated with a main product.
   * Uses the unique cart_add_id to identify which gift wrapping belongs to this specific item.
   * @param {number} mainProductLine - The line number of the main product.
   * @param {number} newQuantity - The new quantity to set for gift wrapping.
   */
  async #updateAssociatedGiftWrappingQuantity(mainProductLine, newQuantity) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (!cart.items || cart.items.length === 0) return;

      const mainProductItem = cart.items[mainProductLine - 1];
      if (!mainProductItem) return;

      const cartAddId = mainProductItem.properties && mainProductItem.properties['_cart_add_id'];
      if (!cartAddId) return;

      /** @type {number[]} */
      const giftWrappingLinesToUpdate = [];

      cart.items.forEach((/** @type {any} */ item, /** @type {number} */ index) => {
        if (index === mainProductLine - 1) return;

        if (item.properties) {
          const itemCartAddId = item.properties['_cart_add_id'];
          const isGiftWrapping = item.properties['_main_product_id'];
          if (isGiftWrapping && itemCartAddId === cartAddId) {
            giftWrappingLinesToUpdate.push(index + 1);
          }
        }
      });

      for (const giftWrappingLine of giftWrappingLinesToUpdate) {
        await this.updateQuantity({
          line: giftWrappingLine,
          quantity: newQuantity,
          action: 'change',
        });
      }
    } catch (error) {
      console.error('Failed to update associated gift wrapping quantity:', error);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
